import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  ReactFlow,
  Node,
  Edge,
  addEdge,
  Connection,
  useNodesState,
  useEdgesState,
  Controls,
  MiniMap,
  Background,
  BackgroundVariant,
  useReactFlow,
  ReactFlowProvider,
} from "reactflow";
import { v4 as uuidv4 } from "uuid";
import { nodeTypes } from "./CustomNodes";
import { NodeEditorModal } from "./NodeEditorModal";
import { WorkflowToolbar } from "./WorkflowToolbar";
import {
  Workflow,
  WorkflowNode,
  WorkflowEdge,
  BackendWorkflowNode,
  BackendWorkflow,
  NodeType,
  CustomNodeData,
  TaskFormData,
} from "../../types";
import { useAuth } from "../../contexts/AuthContext";
import { TaskFormPanel } from "./TaskFormPanel";
import { WorkflowTemplateLibrary } from "./WorkflowTemplateLibrary";
import { SaveTemplateModal } from "./SaveTemplateModal";
import toast from "../../utils/toast";
import { apiService } from "../../services/apiService";
import { useTranslation } from "react-i18next";

interface WorkflowEditorProps {
  workflow?: Workflow;
  onSave?: (workflow: Partial<BackendWorkflow>) => void;
  readonly?: boolean;
  onSaveSuccess?: () => void;
}

const WorkflowEditorComponent: React.FC<WorkflowEditorProps> = ({
  workflow,
  onSave,
  readonly = false,
  onSaveSuccess,
}) => {
  const { t } = useTranslation(["workflows", "common"]);
  const { user } = useAuth();
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const reactFlowInstance = useReactFlow();

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Custom nodes change handler to ensure draggability
  const handleNodesChange = useCallback(
    (changes: any[]) => {
      if (readonly) return;
      onNodesChange(changes);
    },
    [onNodesChange, readonly]
  );
  const [selectedNode, setSelectedNode] = useState<Node<CustomNodeData> | null>(
    null
  );
  const [isNodeEditorOpen, setIsNodeEditorOpen] = useState(false);
  const [showTaskFormPanel, setShowTaskFormPanel] = useState(false);
  const [taskFormNode, setTaskFormNode] = useState<Node<CustomNodeData> | null>(
    null
  );
  const [taskFormViewMode, setTaskFormViewMode] = useState<"edit" | "view">(
    "edit"
  );
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [showLoadTemplateModal, setShowLoadTemplateModal] = useState(false);
  const [isTemplateLoaded, setIsTemplateLoaded] = useState(false);

  const handleEditNode = useCallback((nodeId: string) => {
    // Use functional state update to get the latest nodes
    setNodes((currentNodes) => {
      const node = currentNodes.find((n) => n.id === nodeId);

      if (node) {
        // Handle node editing
        if (
          node.type === NodeType.ACTIVITY ||
          node.type === NodeType.POOL_ACTIVITY
        ) {
          // Open task form panel for activity node in edit mode
          setTaskFormNode(node);
          setTaskFormViewMode("edit");
          setShowTaskFormPanel(true);
        } else {
          // For other node types, open the regular editor
          setSelectedNode(node);
          setIsNodeEditorOpen(true);
        }
      }

      // Always return current nodes unchanged
      return currentNodes;
    });
  }, []);

  const handleViewNode = useCallback((nodeId: string) => {
    // Use functional state update to get the latest nodes
    setNodes((currentNodes) => {
      const node = currentNodes.find((n) => n.id === nodeId);

      if (node) {
        // Handle node viewing
        if (
          node.type === NodeType.ACTIVITY ||
          node.type === NodeType.POOL_ACTIVITY
        ) {
          // Open task form panel for activity node in view mode
          setTaskFormNode(node);
          setTaskFormViewMode("view");
          setShowTaskFormPanel(true);
        }
      }

      // Always return current nodes unchanged
      return currentNodes;
    });
  }, []);

  // Convert workflow data to React Flow format
  useEffect(() => {

    if (workflow && !isTemplateLoaded) {
      const flowNodes: Node<CustomNodeData>[] = workflow.nodes.map(
        (node, index) => ({
          id: node.id,
          type: node.type,
          position: node.position || {
            x: 100 + index * 200,
            y: 100 + Math.floor(index / 3) * 150,
          },
          data: {
            ...(node.data || {}),
            // Convert backend formData to frontend taskFormData
            // Check both node.formData (backend WorkflowNode) and node.data.formData (frontend)
            taskFormData:
              (node as any).formData || node.data?.formData
                ? (node as any).formData || (node.data.formData as TaskFormData)
                : undefined,
            // Map backend activityId to frontend activityDefinitionId for useEffect compatibility
            activityDefinitionId: (() => {
              const backendActivityId = (node as any).activityId;
              const existingActivityDefinitionId = (node.data as any)
                ?.activityDefinitionId;
              const finalActivityDefinitionId =
                backendActivityId || existingActivityDefinitionId;

              return finalActivityDefinitionId;
            })(),
            onEdit: readonly ? undefined : handleEditNode,
            onView: handleViewNode,
            onDelete: readonly ? undefined : handleDeleteNode,
          },
          draggable: !readonly,
          dragHandle: readonly ? undefined : ".drag-handle",
        })
      );

      const flowEdges: Edge[] = workflow.edges.map((edge) => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        sourceHandle: edge.sourceHandle || "bottom",
        targetHandle: edge.targetHandle || "top",
        label: edge.condition
          ? `${edge.condition.field} ${edge.condition.operator} ${edge.condition.value}`
          : undefined,
      }));

      setNodes(flowNodes);
      setEdges(flowEdges);

      // Reset template flag since we just loaded fresh workflow data from backend
      if (isTemplateLoaded) {
        setIsTemplateLoaded(false);
      }
    } else if (workflow && isTemplateLoaded) {
    }
  }, [workflow?.id, workflow?.nodes, readonly]);

  // ✅ Auto-update activity nodes with form data when workflow loads
  useEffect(() => {
    if (!workflow || !workflow.nodes) return;

    const updateActivityNodes = async () => {
      const updatedNodes = await Promise.all(
        workflow.nodes.map(async (node) => {
          // Only update activity nodes that have activityDefinitionId
          const nodeData = node.data as any;

          if (
            nodeData && // ✅ Check if data exists
            (node.type === "ACTIVITY" || node.type === "POOL_ACTIVITY") &&
            nodeData.activityDefinitionId
          ) {

            try {
              // Load activity with forms
              const activity = await apiService.getActivityById(
                nodeData.activityDefinitionId
              );

              // Parse assignedUsers JSON string to array (always do this, regardless of forms)
              let assignedUserIds: number[] = [];
              if (activity.assignedUsers) {
                try {
                  assignedUserIds = JSON.parse(activity.assignedUsers);
                } catch (e) {
                }
              }

              // Build multipleFormsData if forms exist
              let multipleFormsData: any[] = [];
              if (activity.activityForms && activity.activityForms.length > 0) {
                multipleFormsData = activity.activityForms.map(
                  (activityForm: any, index: number) => ({
                    id: activityForm.form.id,
                    title: activityForm.form.title,
                    description: activityForm.form.description || "",
                    formStructure: activityForm.form.formStructure,
                    submissionData: activityForm.form.submissionData || {},
                    displayOrder: activityForm.displayOrder || index,
                    isFromLibrary: false,
                  })
                );
              }

              // Parse attachments from JSON string to array
              let attachments = [];
              if (activity.attachments) {
                try {
                  attachments = JSON.parse(activity.attachments);
                } catch (e) {
                }
              }

              // Create taskFormData for the node (always, even if no forms)
              const taskFormData = {
                activityName: activity.title,
                activityDescription: activity.description || "",
                activityId: activity.id,
                formName: activity.title,
                formDescription: activity.description || "",
                elements: [],
                elementOrder: "[]",
                multipleFormsData: multipleFormsData,
                startDate: activity.startDate,
                endDate: activity.endDate,
                assignedUserIds: assignedUserIds,
                attachments: attachments,
              };

              return {
                ...node,
                data: {
                  ...node.data,
                  taskFormData: taskFormData,
                },
              };
            } catch (error) {}
          }

          return node; // Return unchanged if not an activity node
        })
      );

      // Update nodes with loaded data
      const convertedNodes: Node[] = updatedNodes.map((node) => ({
        id: node.id,
        type: (node.type as NodeType) || "ACTIVITY",
        position: { x: node.position?.x || 0, y: node.position?.y || 0 },
        data: {
          ...node.data,
          onEdit: readonly ? undefined : handleEditNode,
          onView: handleViewNode,
          onDelete: readonly ? undefined : handleDeleteNode,
        },
        dragHandle: readonly ? undefined : ".drag-handle",
        draggable: !readonly,
      }));

      setNodes(convertedNodes);
    };

    updateActivityNodes();
  }, [workflow?.id, workflow?.nodes]); // Trigger when workflow or nodes change

  const onConnect = useCallback(
    (params: Connection) => {
      if (readonly) return;

      // Ensure sourceHandle and targetHandle are properly set
      const enhancedParams = {
        ...params,
        sourceHandle: params.sourceHandle || "bottom",
        targetHandle: params.targetHandle || "top",
      };

      setEdges((eds) => addEdge(enhancedParams, eds));
    },
    [setEdges, readonly]
  );

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      if (readonly) return;

      event.preventDefault();

      const reactFlowBounds = reactFlowWrapper.current?.getBoundingClientRect();
      const type = event.dataTransfer.getData("application/reactflow");

      if (typeof type === "undefined" || !type || !reactFlowBounds) {
        return;
      }

      const position = reactFlowInstance.project({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      });

      const newNode: Node<CustomNodeData> = {
        id: `${type}-${uuidv4()}`,
        type,
        position,
        data: {
          label: `New ${type.charAt(0).toUpperCase() + type.slice(1)}`,
          description: "",
          onEdit: handleEditNode,
          onView: handleViewNode,
          onDelete: handleDeleteNode,
        },
        draggable: !readonly,
        dragHandle: readonly ? undefined : ".drag-handle",
      };

      setNodes((nds) => nds.concat(newNode));
    },
    [
      reactFlowInstance,
      setNodes,
      readonly,
      handleEditNode,
    ]
  );

  const handleSaveNode = (nodeData: CustomNodeData) => {
    if (!selectedNode) return;

    setNodes((nds) =>
      nds.map((node) =>
        node.id === selectedNode.id
          ? {
              ...node,
              data: {
                ...nodeData,
                onEdit: handleEditNode,
                onView: handleViewNode,
              },
              draggable: !readonly,
              dragHandle: readonly ? undefined : ".drag-handle",
            }
          : node
      )
    );

    setIsNodeEditorOpen(false);
    setSelectedNode(null);
  };

  const handleTaskFormSave = (
    nodeId: string,
    formData: TaskFormData,
    updatedNodeData?: Partial<CustomNodeData>
  ) => {
    setNodes((nds) =>
      nds.map((node) =>
        node.id === nodeId
          ? {
              ...node,
              data: {
                ...node.data,
                // Apply any additional node data updates if provided
                ...updatedNodeData,
                label:
                  formData.activityName || formData.formName || node.data.label,
                description:
                  formData.activityDescription ||
                  formData.formDescription ||
                  node.data.description,
                taskFormData: formData,
                formData: formData, // Also save to backend-compatible field
                onEdit: handleEditNode,
                onView: handleViewNode,
              },
              draggable: !readonly,
              dragHandle: readonly ? undefined : ".drag-handle",
            }
          : node
      )
    );
  };

  const handleCloseTaskFormPanel = () => {
    setShowTaskFormPanel(false);
    setTaskFormNode(null);
  };

  const handleDeleteNode = (nodeId?: string) => {
    const idToDelete = nodeId || selectedNode?.id;
    if (!idToDelete) return;

    // Node'u workflow'dan kaldır (activities tablosundan silmez!)
    setNodes((nds) => nds.filter((node) => node.id !== idToDelete));

    // İlgili edge'leri de temizle
    setEdges((eds) =>
      eds.filter(
        (edge) => edge.source !== idToDelete && edge.target !== idToDelete
      )
    );

    // Eğer modal açıksa kapat
    if (isNodeEditorOpen && selectedNode?.id === idToDelete) {
      setIsNodeEditorOpen(false);
      setSelectedNode(null);
    }
  };

  const handleSaveWorkflow = async () => {
    if (!onSave) return;

    try {
      // 🆕 STEP 1: Process pending activities before saving workflow
      const pendingNodes = nodes.filter(
        (node) =>
          node.data.isPendingActivity === true &&
          (node.type === NodeType.ACTIVITY ||
            node.type === NodeType.POOL_ACTIVITY)
      );

      if (pendingNodes.length > 0) {
        const loadingToast = toast.loading(
          `Saving ${pendingNodes.length} pending activities...`
        );

        // Process each pending node
        for (const node of pendingNodes) {

          try {
            // Step 1: Save all pending forms
            const savedFormIds: number[] = [];
            const pendingForms = node.data.pendingForms || [];

            for (const form of pendingForms) {
              // Check if form already has a valid ID (already saved)
              const isExistingForm =
                form.id && typeof form.id === "number" && form.id > 0;

              if (isExistingForm) {
                // Form already exists in database
                savedFormIds.push(form.id);
              } else {
                // Save new form
                const savedForm = await apiService.createForm({
                  title: form.title,
                  description: form.description || "",
                  formStructure: form.formStructure,
                  submissionData: form.submissionData || {},
                  isPublic: false,
                  version: 1,
                });
                savedFormIds.push(savedForm.id);
              }
            }

            // Step 2: Create activity
            const activityData = {
              title: node.data.taskFormData?.activityName || node.data.label,
              description:
                node.data.taskFormData?.activityDescription ||
                node.data.description ||
                "",
              isPublic: false,
              requiresApproval: node.data?.approvalRequired === true,
              isLeave: Boolean(node.data.taskFormData?.isLeave),
              version: 1,
              startDate: node.data.taskFormData?.startDate,
              endDate: node.data.taskFormData?.endDate,
              assignedUsers: node.data.taskFormData?.assignedUserIds
                ? JSON.stringify(node.data.taskFormData.assignedUserIds)
                : undefined,
              attachments: (() => {
                const atts = node.data.taskFormData?.attachments;
                const result = atts ? JSON.stringify(atts) : undefined;
                return result;
              })(),
            };
            const savedActivity = await apiService.createActivity(activityData);

            // Step 3: Link forms to activity
            for (let i = 0; i < savedFormIds.length; i++) {
              await apiService.addFormToActivity(
                savedActivity.id,
                savedFormIds[i],
                i
              );
            }

            // Step 4: Update node data to remove pending flags
            node.data.activityDefinitionId = savedActivity.id;
            node.data.isPendingActivity = false;
            node.data.pendingForms = [];
            if (node.data.taskFormData) {
              node.data.taskFormData.activityId = savedActivity.id;

              // ✅ CRITICAL FIX: Restore attachments from saved activity
              if (savedActivity.attachments) {
                try {
                  const attachmentsArray = JSON.parse(
                    savedActivity.attachments
                  );
                  node.data.taskFormData.attachments = attachmentsArray;
                } catch (e) {
                }
              }
            }
          } catch (error) {
            toast.dismiss(loadingToast);
            toast.error(
              t("workflows:failedToSavePendingActivity", {
                label: node.data.label,
              })
            );
            return; // Stop workflow save if activity save fails
          }
        }

        toast.dismiss(loadingToast);
        toast.success(
          t("workflows:pendingActivitiesSaved", { count: pendingNodes.length })
        );
      }

      // 🆕 STEP 2: Now save the workflow with updated nodes
      const workflowNodes: BackendWorkflowNode[] = nodes.map((node) => ({
        id: node.id,
        type: (node.type as string).startsWith("PROJECT_")
          ? (node.type as NodeType) // PROJECT types are already lowercase in enum
          : ((node.type as string).toLowerCase() as NodeType), // Convert regular types to lowercase for backend
        positionX: node.position.x,
        positionY: node.position.y,
        label: node.data?.label || "",
        description: node.data?.description || "",
        assigneeId: node.data?.assigneeId,
        assigneeName: node.data?.assigneeName,
        activityId:
          node.data?.taskFormData?.activityId ||
          node.data?.activityDefinitionId ||
          node.data?.activityDefinition?.id,
        // ✅ Save taskFormData to formData field for persistence
        formData: node.data?.taskFormData
          ? {
              ...node.data.taskFormData,
              // Ensure all important fields are preserved
              activityName: node.data.taskFormData.activityName,
              activityDescription: node.data.taskFormData.activityDescription,
              startDate: node.data.taskFormData.startDate,
              endDate: node.data.taskFormData.endDate,
              assignedUserIds: node.data.taskFormData.assignedUserIds,
            }
          : undefined,
        conditions: node.data?.conditions,
        notificationMessage: node.data?.notificationMessage,
        approvalRequired: node.data?.approvalRequired,
        dueDate: node.data?.dueDate,
      }));

      const workflowEdges: WorkflowEdge[] = edges.map((edge) => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        sourceHandle: edge.sourceHandle,
        targetHandle: edge.targetHandle,
      }));

      const workflowData: Partial<BackendWorkflow> = {
        id: workflow?.id,
        name: workflow?.name || "Untitled Workflow",
        nodes: workflowNodes,
        edges: workflowEdges,
        createdBy: user?.id,
        // Preserve existing workflow metadata during updates
        workflowContainerId: workflow?.workflowContainerId,
        isActive: workflow?.isActive,
      };

      onSave(workflowData);

      // Don't immediately reset template flag - let it reset naturally when new workflow data loads

      toast.success(t("workflows:workflowSavedSuccessfully"));
    } catch (error) {
      toast.error(t("workflows:workflowSaveFailed"));
    }
  };

  const handleAddActivityFromPool = async (activity: any) => {
    try {

      // Parse assignedUsers JSON string to array
      let assignedUserIds: number[] = [];
      if (activity.assignedUsers) {
        try {
          assignedUserIds = JSON.parse(activity.assignedUsers);
        } catch (e) {
        }
      }

      // Build pending forms data from activity forms
      const pendingForms =
        activity.activityForms?.map((activityForm: any, index: number) => ({
          id: activityForm.form.id < 0 ? undefined : activityForm.form.id, // Remove negative temp IDs
          title: activityForm.form.title,
          description: activityForm.form.description || "",
          formStructure: activityForm.form.formStructure,
          submissionData: activityForm.form.submissionData || {},
          displayOrder: activityForm.displayOrder || index,
          isFromLibrary: false,
        })) || [];

      // Parse attachments from activity
      let attachments: any[] = [];
      if (activity.attachments) {
        try {
          attachments = JSON.parse(activity.attachments);
        } catch (e) {
        }
      }

      // Build taskFormData for display
      const taskFormData = {
        activityName: activity.title,
        activityDescription: activity.description || "",
        activityId: undefined, // Will be set when saved
        formName: activity.title,
        formDescription: activity.description || "",
        elements: [],
        elementOrder: "[]",
        multipleFormsData: pendingForms,
        startDate: activity.startDate,
        endDate: activity.endDate,
        assignedUserIds: assignedUserIds,
        attachments: attachments, // ✅ Include attachments from activity/template
      };

      // Create a pending node (will be saved when workflow is saved)
      const newNode: Node<CustomNodeData> = {
        id: `activity-${uuidv4()}`,
        type: NodeType.ACTIVITY,
        position: { x: 100, y: 100 }, // Default position, user can drag
        data: {
          label: activity.title,
          description: activity.description,
          taskFormData: taskFormData,
          isPendingActivity: true, // Mark as pending
          pendingForms: pendingForms, // Forms to be saved
          onEdit: handleEditNode,
          onView: handleViewNode,
          onDelete: handleDeleteNode,
        },
        draggable: !readonly,
        dragHandle: readonly ? undefined : ".drag-handle",
      };

      setNodes((nds) => nds.concat(newNode));
      toast.success(
        t("workflows:activityAddedToWorkflow", { title: activity.title })
      );
    } catch (error) {
      toast.error(t("workflows:errorAddingActivityFromPool"));
    }
  };

  // Workflow Template Handlers
  const handleSaveAsTemplate = () => {
    if (nodes.length === 0 && edges.length === 0) {
      toast.error(t("workflows:cannotSaveEmptyWorkflow"));
      return;
    }

    setShowSaveTemplateModal(true);
  };

  const handleSaveTemplate = async (templateData: {
    title: string;
    description: string;
    category: string;
    tags: string;
    isPublic: boolean;
  }) => {
    try {
      const fullTemplateData = {
        ...templateData,
        nodeTemplates: nodes.map((node) => {
          // Clean node data to remove functions and non-serializable objects
          const cleanedData: any = {};

          // Copy only serializable fields
          if (node.data.label) cleanedData.label = node.data.label;
          if (node.data.description)
            cleanedData.description = node.data.description;
          if (
            node.data.assigneeId !== undefined &&
            node.data.assigneeId !== null
          )
            cleanedData.assigneeId = node.data.assigneeId;
          if (
            node.data.assigneeName !== undefined &&
            node.data.assigneeName !== null
          )
            cleanedData.assigneeName = node.data.assigneeName;
          if (
            node.data.activityDefinitionId !== undefined &&
            node.data.activityDefinitionId !== null
          )
            cleanedData.activityDefinitionId = node.data.activityDefinitionId;
          if (
            node.data.conditions !== undefined &&
            node.data.conditions !== null
          )
            cleanedData.conditions = node.data.conditions;
          if (
            node.data.notificationMessage !== undefined &&
            node.data.notificationMessage !== null
          )
            cleanedData.notificationMessage = node.data.notificationMessage;
          if (node.data.approvalRequired !== undefined)
            cleanedData.approvalRequired = node.data.approvalRequired;
          if (node.data.dueDate !== undefined && node.data.dueDate !== null)
            cleanedData.dueDate = node.data.dueDate;
          if (
            node.data.budgetThreshold !== undefined &&
            node.data.budgetThreshold !== null
          )
            cleanedData.budgetThreshold = node.data.budgetThreshold;
          if (node.data.archiveConfirmation !== undefined)
            cleanedData.archiveConfirmation = node.data.archiveConfirmation;

          // Handle taskFormData - serialize separately if exists
          if (node.data.taskFormData) {
            try {
              cleanedData.taskFormData = {
                activityId: node.data.taskFormData.activityId,
                activityName: node.data.taskFormData.activityName,
                activityDescription: node.data.taskFormData.activityDescription,
                formName: node.data.taskFormData.formName,
                formDescription: node.data.taskFormData.formDescription,
                elements: node.data.taskFormData.elements,
                elementOrder: node.data.taskFormData.elementOrder,
                submissionData: node.data.taskFormData.submissionData,
                multipleFormsData: node.data.taskFormData.multipleFormsData,
                startDate: node.data.taskFormData.startDate,
                endDate: node.data.taskFormData.endDate,
                assignedUserIds: node.data.taskFormData.assignedUserIds,
              };
            } catch (e) {}
          }

          const nodeTemplate = {
            nodeId: node.id,
            nodeType: node.type,
            positionX: Number(node.position.x),
            positionY: Number(node.position.y),
            nodeData: JSON.stringify(cleanedData),
          };

          return nodeTemplate;
        }),
        edgeTemplates: edges.map((edge) => ({
          edgeId: edge.id,
          sourceNodeId: edge.source,
          targetNodeId: edge.target,
          edgeData: JSON.stringify({
            label: edge.label,
            animated: edge.animated,
            sourceHandle: edge.sourceHandle,
            targetHandle: edge.targetHandle,
          }),
        })),
      };

      const result = await apiService.createWorkflowTemplate(fullTemplateData);
      toast.success(
        t("workflows:workflowTemplateSaved", { title: templateData.title })
      );
    } catch (error) {
      toast.error(
        t("workflows:errorSavingWorkflowTemplate", {
          message: (error as Error).message,
        })
      );
    }
  };

  const handleLoadTemplate = () => {
    setShowLoadTemplateModal(true);
  };

  const handleTemplateSelect = async (template: any) => {
    try {

      // Load template details
      const templateDetails = await apiService.getWorkflowTemplateById(
        template.id
      );

      // Generate unique IDs for nodes and edges to avoid conflicts with existing workflow nodes
      const nodeIdMap = new Map<string, string>();

      // Convert template nodes to React Flow nodes with new unique IDs
      const templateNodes: Node<CustomNodeData>[] =
        templateDetails.nodeTemplates.map(
          (nodeTemplate: any, index: number) => {
            const nodeData = JSON.parse(nodeTemplate.nodeData);

            // Generate new unique node ID
            const newNodeId = `template-node-${Date.now()}-${index}`;
            nodeIdMap.set(nodeTemplate.nodeId, newNodeId);

            return {
              id: newNodeId,
              type: nodeTemplate.nodeType,
              position: {
                x: Number(nodeTemplate.positionX),
                y: Number(nodeTemplate.positionY),
              },
              data: {
                ...nodeData,
                // Ensure proper backend format for workflow nodes
                label: nodeData.label || nodeTemplate.nodeType,
                description: nodeData.description || "",
                assigneeId: nodeData.assigneeId || null,
                assigneeName: nodeData.assigneeName || null,
                activityId:
                  nodeData.activityDefinitionId || nodeData.activityId || null,
                // Re-add the callback functions
                onEdit: readonly ? undefined : handleEditNode,
                onView: handleViewNode,
                onDelete: readonly ? undefined : handleDeleteNode,
              },
              draggable: !readonly,
              dragHandle: readonly ? undefined : ".drag-handle",
            };
          }
        );

      // Convert template edges to React Flow edges with updated node IDs
      const templateEdges: Edge[] = templateDetails.edgeTemplates.map(
        (edgeTemplate: any, index: number) => {
          const edgeData = JSON.parse(edgeTemplate.edgeData);

          // Generate new unique edge ID and map to new node IDs
          const newEdgeId = `template-edge-${Date.now()}-${index}`;
          const newSourceId =
            nodeIdMap.get(edgeTemplate.sourceNodeId) ||
            edgeTemplate.sourceNodeId;
          const newTargetId =
            nodeIdMap.get(edgeTemplate.targetNodeId) ||
            edgeTemplate.targetNodeId;

          return {
            id: newEdgeId,
            source: newSourceId,
            target: newTargetId,
            sourceHandle: edgeData.sourceHandle || "bottom",
            targetHandle: edgeData.targetHandle || "top",
            label: edgeData.label,
            animated: edgeData.animated,
          };
        }
      );

      setNodes(templateNodes);
      setEdges(templateEdges);
      setIsTemplateLoaded(true);
      setShowLoadTemplateModal(false);

      toast.success(
        t("workflows:templateLoadedSuccessfully", { title: template.title })
      );
    } catch (error) {
      toast.error(
        t("workflows:errorLoadingWorkflowTemplate", {
          message: (error as Error).message,
        })
      );
    }
  };

  return (
    <div className="h-full flex flex-col">
      {!readonly && (
        <WorkflowToolbar
          onSave={handleSaveWorkflow}
          canSave={!!onSave}
          onAddActivityFromPool={handleAddActivityFromPool}
          onSaveAsTemplate={readonly ? undefined : handleSaveAsTemplate}
          onLoadTemplate={readonly ? undefined : handleLoadTemplate}
        />
      )}

      <div className="flex-1 flex">
        <div className="flex-1" ref={reactFlowWrapper}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={handleNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onNodesDelete={(deleted) => {
              if (deleted.length > 0 && !readonly) {
                const confirmMessage =
                  deleted.length === 1
                    ? `"${
                        deleted[0].data.label || "Bu aktiviteyi"
                      }" kalıcı olarak silmek istediğinize emin misiniz?\n\nBu işlem geri alınamaz ve aktivite veritabanından silinecektir.`
                    : `${deleted.length} aktiviteyi kalıcı olarak silmek istediğinize emin misiniz?\n\nBu işlem geri alınamaz ve aktiviteler veritabanından silinecektir.`;

                if (!window.confirm(confirmMessage)) {
                  // User cancelled - prevent deletion by restoring nodes
                  // Note: ReactFlow doesn't support cancelling onNodesDelete,
                  // so we need to use onNodesChange instead for better control
                  return;
                }
              }
            }}
            nodeTypes={nodeTypes}
            fitView
            deleteKeyCode={readonly ? null : "Delete"}
            nodesDraggable={!readonly}
            nodesConnectable={!readonly}
            elementsSelectable={true}
          >
            <Controls />
            <MiniMap />
            <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
          </ReactFlow>
        </div>

        {/* Task Form Panel */}
        {showTaskFormPanel && taskFormNode && (
          <TaskFormPanel
            nodeData={taskFormNode.data}
            nodeId={taskFormNode.id}
            onClose={handleCloseTaskFormPanel}
            onFormSave={handleTaskFormSave}
            readonly={readonly || taskFormViewMode === "view"}
            viewMode={taskFormViewMode}
            projectId={workflow?.workflowContainerId}
          />
        )}

        {/* Load Template Modal */}
        {showLoadTemplateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg w-full max-w-6xl h-5/6 mx-4 flex flex-col">
              <div className="flex justify-between items-center p-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Load Workflow Template
                </h3>
                <button
                  onClick={() => setShowLoadTemplateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              <div className="flex-1 overflow-hidden p-4">
                <WorkflowTemplateLibrary
                  onSelectTemplate={handleTemplateSelect}
                  onClose={() => setShowLoadTemplateModal(false)}
                />
              </div>
            </div>
          </div>
        )}

        {/* Save Template Modal */}
        <SaveTemplateModal
          isOpen={showSaveTemplateModal}
          onClose={() => setShowSaveTemplateModal(false)}
          onSave={handleSaveTemplate}
        />
      </div>

      {isNodeEditorOpen && selectedNode && (
        <NodeEditorModal
          node={selectedNode}
          onSave={handleSaveNode}
          onDelete={handleDeleteNode}
          onClose={() => {
            setIsNodeEditorOpen(false);
            setSelectedNode(null);
          }}
        />
      )}
    </div>
  );
};

export const WorkflowEditor: React.FC<WorkflowEditorProps> = (props) => {
  return (
    <ReactFlowProvider>
      <WorkflowEditorComponent {...props} />
    </ReactFlowProvider>
  );
};
